// Домейн-модель Trip Builder. Единственный источник истины по контракту.
// Файл не менять без согласования — от него зависят все модули.

/**
 * Код валюты — 3 заглавные буквы. Список не фиксирован: пользователь заводит
 * свои валюты, поэтому это строка, а не union. Единственный жёсткий код — BASE_CURRENCY.
 */
export type Currency = string;

export const CURRENCY_CODE_RE = /^[A-Z]{3}$/;

/** Базовая валюта справочника: её курс всегда 1 и удалить её нельзя. */
export const BASE_CURRENCY = 'USD';

/** Запись справочника валют. Курс — сколько единиц BASE_CURRENCY в одной единице `code`. */
export interface CurrencyDef {
  code: Currency;
  /** символ для отображения ('$', '€', '£'); пустой — печатаем код */
  symbol: string;
  rateToBase: number;
}

export const DEFAULT_CURRENCIES: readonly CurrencyDef[] = [
  { code: 'USD', symbol: '$', rateToBase: 1 },
  { code: 'EUR', symbol: '€', rateToBase: 1.08 },
];

/** Деньги всегда в минорных единицах (центах). Никаких float для сумм. */
export interface Money {
  amount: number;
  currency: Currency;
}

export type SegmentKind =
  | 'flight'
  | 'transfer'
  | 'lodging'
  | 'taxi'
  | 'food'
  | 'baggage'
  | 'misc';

export type PriceScope = 'one_way' | 'round_trip_half' | 'round_trip_full';

/** Откуда блок попал в библиотеку: руками или разбором источника. */
export type SegmentOrigin = 'manual' | 'text' | 'url' | 'screenshot';

/**
 * Общее у всех блоков библиотеки: когда блок в неё попал и откуда.
 *
 * `addedAt` — момент добавления (epoch ms). По нему библиотека сортируется
 * «новые сверху» и метит свежие блоки лейблом new. Поле необязательное:
 * состояния, сохранённые до его появления, читаются как есть, а блок без
 * отметки считается старым.
 */
export interface SegmentMeta {
  addedAt?: number;
  origin?: SegmentOrigin;
}

export interface FlightSegment extends SegmentMeta {
  kind: 'flight';
  id: string;
  from: string; // IATA, 3 буквы uppercase
  to: string; // IATA
  fromTz: string; // IANA timezone, '' если неизвестна
  toTz: string; // IANA timezone, '' если неизвестна
  departLocal: string; // '2026-12-27T11:55' — ISO без офсета
  arriveLocal: string; // локальное время прилёта
  /** Перевозчик; '' — неизвестен: рейс опознают по маршруту и времени. */
  carrier: string;
  flightNumber?: string;
  price: Money;
  priceScope: PriceScope;
  /** Ссылка на рейс или на купленный билет — чтобы вернуться к источнику цены. */
  url?: string;
  /** id парного RT-сегмента (для round_trip_full) */
  linkedTo?: string;
  note?: string;
}

export interface TransferSegment extends SegmentMeta {
  kind: 'transfer';
  id: string;
  from: string; // свободный текст
  to: string;
  durationMin: number;
  price: Money;
  /** цена за человека (по умолчанию false) */
  perPerson?: boolean;
  note?: string;
}

export interface LodgingSegment extends SegmentMeta {
  kind: 'lodging';
  id: string;
  city: string;
  nights: number;
  pricePerNight: Money;
  /** цена за человека (по умолчанию false) */
  perPerson?: boolean;
  /**
   * «На весь промежуток»: ночи считаются автоматически — от прилёта
   * предыдущего рейса варианта до вылета следующего (не больше 365).
   * `nights` при этом остаётся запасным значением на случай, когда якорей
   * в варианте нет (см. src/core/calc.ts: wholeStayNights).
   */
  wholeStay?: boolean;
  note?: string;
}

export interface SimpleCostSegment extends SegmentMeta {
  kind: 'taxi' | 'food' | 'misc';
  id: string;
  label: string;
  city?: string;
  price: Money;
  quantity: number; // по умолчанию 1; для food — дни
  note?: string;
}

export interface BaggageSegment extends SegmentMeta {
  kind: 'baggage';
  id: string;
  flightId: string;
  label: string;
  price: Money;
}

export type Segment =
  | FlightSegment
  | TransferSegment
  | LodgingSegment
  | SimpleCostSegment
  | BaggageSegment;

export interface TripVariant {
  id: string;
  name: string;
  color: string; // hex
  segmentIds: string[]; // порядок = порядок на таймлайне
  /** вариант защищён от изменений (замок в шапке); по умолчанию false */
  locked?: boolean;
  /** тело варианта свёрнуто в аккордеоне; по умолчанию false */
  collapsed?: boolean;
}

export interface AppState {
  library: Record<string, Segment>;
  variants: TripVariant[]; // до 4
  travelers: number;
  displayCurrency: Currency;
  /** справочник валют; всегда содержит BASE_CURRENCY с курсом 1 */
  currencies: CurrencyDef[];
}

export const MAX_VARIANTS = 4;
export const DEFAULT_EUR_USD = 1.08;

/** Порог рискованной стыковки, минуты: меньше двух часов до следующего рейса. */
export const RISKY_CONNECTION_MIN = 120;
/** Порог долгого ожидания, минуты. */
export const LONG_LAYOVER_MIN = 20 * 60;

/** Типы, которые умножаются на количество путешественников безусловно. */
export const PER_TRAVELER_KINDS: SegmentKind[] = [
  'flight',
  'baggage',
  'food',
  'taxi',
];

// ─────────────────────────── Расчёты: контракт ───────────────────────────

export type WarningLevel = 'warn' | 'error';

export type WarningCode =
  | 'need_transfer' // аэропорты не стыкуются, переезда нет
  | 'risky_connection' // < 2 часов до следующего рейса
  | 'negative_connection' // вылет раньше прилёта
  | 'long_layover' // > 20 часов без ночёвки
  | 'orphan_baggage' // багаж ссылается на рейс вне варианта
  | 'rt_full_unpaired' // round_trip_full без пары в варианте
  | 'missing_tz'; // неизвестная таймзона, длительность не посчитана

export interface TripWarning {
  code: WarningCode;
  level: WarningLevel;
  message: string;
  /** id сегмента, к которому привязано предупреждение (для бейджа на карточке) */
  segmentId?: string;
  /** для стыков: индекс "щели" в variant.segmentIds — предупреждение живёт ПОСЛЕ этого индекса */
  gapIndex?: number;
}

/** Стык между двумя соседними flight-сегментами варианта. */
export interface ConnectionInfo {
  /** индекс "щели": между segmentIds[gapIndex] и segmentIds[gapIndex + 1] */
  gapIndex: number;
  fromSegmentId: string;
  toSegmentId: string;
  /** минуты между прилётом и следующим вылетом; null если таймзоны неизвестны */
  layoverMin: number | null;
  /** есть ли между рейсами transfer-сегмент */
  hasTransfer: boolean;
  /** есть ли между рейсами lodging-сегмент */
  hasLodging: boolean;
  /** аэропорты стыкуются напрямую */
  sameAirport: boolean;
  warnings: TripWarning[];
}

/** Разбивка стоимости варианта по типам сегментов, в минорных единицах displayCurrency. */
export interface CostBreakdown {
  flight: number;
  transfer: number;
  lodging: number;
  taxi: number;
  food: number;
  baggage: number;
  misc: number;
}

export interface VariantTotals {
  variantId: string;
  /** итого без багажа */
  totalMinor: number;
  /** итого с багажом */
  totalWithBaggageMinor: number;
  breakdown: CostBreakdown;
  currency: Currency;
  /** применялась ли конвертация валют хотя бы к одному сегменту */
  converted: boolean;
  /** чистый налёт в минутах (сумма длительностей flight) */
  flightTimeMin: number;
  /** дверь-в-дверь: от вылета первого flight до прилёта последнего, минуты */
  doorToDoorMin: number;
  /** сумма ночей lodging */
  nights: number;
  warnings: TripWarning[];
  connections: ConnectionInfo[];
  /** стоимость каждого сегмента варианта в displayCurrency (минорные), с учётом
   *  travelers/quantity/nights/RT-парности. RT-пара, посчитанная в партнёре, даёт 0. */
  perSegmentMinor: Record<string, number>;
}
